import React, { useMemo, useCallback } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import size from "lodash/size";
import { getAllFiltersWithCounts, filterResults } from '../../utils/filterUtils';

// All smart filters refer to a base filter key only
const SMART_FILTERS = [
  { key: "only_auto", baseFilter: "transmission", value: "A", labelKey: "smartFilters.onlyAuto" },
  { key: "zero_excess", baseFilter: "insurance", value: "zeroExcess", labelKey: "smartFilters.zeroExcess" },
  { key: "excess_refund", baseFilter: "insurance", value: "excessRefundIncluded", labelKey: "smartFilters.excessRefundIncluded" },
  { key: "wagon", baseFilter: "carType", value: "W", labelKey: "smartFilters.wagon" },
  { key: "suv", baseFilter: "carType", value: "F|G", labelKey: "smartFilters.suv" },
  { key: "desk_terminal", baseFilter: "deskLocation", value: "inTerminal", labelKey: "smartFilters.terminalOnly" },
  { key: "recommended_supplier", baseFilter: "recommendedSupplier", value: "recommended", labelKey: "smartFilters.recommendedSupplier" }
];

const SmartFilterWidget = ({ data = [], onChange, activeFilters = {}, dir, onClear, isAirport = false }) => {
  const { t, i18n } = useTranslation();
  const totalCount = data.length;
  const locale = i18n.language;

  // Use the shared utility to get all filters with counts
  const allFilters = getAllFiltersWithCounts({ data, values: activeFilters, isAirport, locale });

  const suggestions = useMemo(() => {
    return SMART_FILTERS.map(f => {
      const filterDef = allFilters[f.baseFilter];
      if (!filterDef) return null;
      const item = filterDef.items.find(i => String(i.value) === String(f.value));
      const filterValue = activeFilters[f.baseFilter] || [];
      const isActive = filterValue.includes(f.value);
      return {
        key: f.key,
        label: t(f.labelKey),
        baseFilter: f.baseFilter,
        value: f.value,
        isActive,
        matchCount: item ? item.count : 0
      };
    }).filter(Boolean);
  }, [allFilters, activeFilters, t]);

  const filteredData = useMemo(() => filterResults(data, activeFilters), [data, activeFilters]);

  const hasActiveSuggestions = suggestions.some(s => s.isActive);
  const activeCount = filteredData.length;

  const handleToggle = useCallback((baseFilter, value, event) => {
    const smartValuesForField = suggestions.filter(s => s.baseFilter === baseFilter).map(s => s.value);
    const oldValue = Array.isArray(activeFilters[baseFilter]) ? activeFilters[baseFilter] : [activeFilters[baseFilter]].filter(Boolean);
    const newValue = oldValue.includes(value)
      ? oldValue.filter(v => v !== value)
      : [...oldValue.filter(v => smartValuesForField.includes(v)), value];

    onChange({ [baseFilter]: newValue });
    if (window.gtag) {
      window.gtag('event', 'Smart Filter Changed', {
        'event_category': 'SmartFilters',
        'event_label': baseFilter,
        'value': value
      });
    }
    const button = event?.currentTarget;
    if (button) setTimeout(() => button.blur(), 200);
  }, [onChange, suggestions, activeFilters]);

  if (size(suggestions) === 0) return null;

  return (
    <div className={dir === "rtl" ? "pr-md-3" : "pl-md-3"}>
      <Alert variant="info" className="smart-filter-widget">
        <div className="mb-2 d-flex justify-content-between align-items-center">
          <span className={hasActiveSuggestions ? "fw-bold text-primary" : ""}>
            🔍 {t("smartFilters.suggestionTitle")}
            {hasActiveSuggestions && <span className="ms-1 text-danger">●</span>}
          </span>
        </div>

        <div className="text-muted small mb-2">
          {t("smartFilters.visibleResults", { count: activeCount, total: totalCount })}<br />
          {t("smartFilters.tip")}
        </div>

        <div className="pb-2 d-block d-md-none overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="d-flex flex-nowrap" style={{ minWidth: 'max-content', gap: '0.5rem' }}>
            {suggestions.map(sugg => (
              <button
                key={sugg.key}
                type="button"
                className={`btn btn-sm rounded-pill ${sugg.isActive ? "btn-primary" : "btn-light"}`}
                onClick={e => handleToggle(sugg.baseFilter, sugg.value, e)}
                style={{ marginRight: '0.5rem', transition: 'background-color 0.3s ease' }}
              >
                {sugg.label} <small className="ms-1 text-reset">({sugg.matchCount})</small>
              </button>
            ))}
          </div>
        </div>

        <div className="d-none d-md-flex flex-wrap" style={{ gap: '0.5rem' }}>
          {suggestions.map(sugg => (
            <button
              key={sugg.key}
              type="button"
              className={`btn btn-sm rounded-pill ${sugg.isActive ? "btn-primary" : "btn-light"}`}
              onClick={e => handleToggle(sugg.baseFilter, sugg.value, e)}
              style={{ transition: 'background-color 0.3s ease' }}
            >
              {sugg.label} <small className="ms-1 text-reset">({sugg.matchCount})</small>
            </button>
          ))}
        </div>

        <div className="text-muted small mt-1 d-block d-md-none" style={{ fontStyle: 'italic' }}>{t("smartFilters.scrollingAllowed")}</div>

        {onClear && hasActiveSuggestions && (
          <div className="text-end mt-3 fade-in">
            <Button size="sm" variant="outline-danger" onClick={onClear}>
              {t("smartFilters.clearFilters")}
            </Button>
          </div>
        )}
      </Alert>
    </div>
  );
};

export default SmartFilterWidget;
